import { StyleSheet } from '@react-pdf/renderer';

export const pdfColors = {
    text: '#231f20',
    muted: '#5c534c',
    border: '#e8e0d6',
    accent: '#c4603c',
};

/** Round scaled point sizes for stable PDF layout. */
function s(scale, n) {
    return Math.round(n * scale * 10) / 10;
}

/**
 * @param {number} [scale=1] Layout scale (0.65–1). Smaller values reflow content onto fewer pages.
 */
export function createPdfStyles(scale = 1) {
    return StyleSheet.create({
        page: {
            fontFamily: 'Helvetica',
            fontSize: s(scale, 9),
            color: pdfColors.text,
            paddingTop: s(scale, 42),
            paddingBottom: s(scale, 32),
            paddingHorizontal: s(scale, 40),
        },
        h1: {
            fontSize: s(scale, 14),
            fontWeight: 'bold',
            marginBottom: s(scale, 4),
            textTransform: 'uppercase',
        },
        subtitle: {
            fontSize: s(scale, 9),
            color: pdfColors.muted,
            marginBottom: s(scale, 14),
        },
        headerRow: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginBottom: s(scale, 16),
            paddingBottom: s(scale, 10),
            borderBottomWidth: 1,
            borderBottomColor: pdfColors.border,
        },
        brandBlock: {
            maxWidth: '58%',
        },
        brandTitle: {
            fontSize: s(scale, 11),
            fontWeight: 'bold',
            marginBottom: s(scale, 2),
        },
        brandLine: {
            fontSize: s(scale, 8),
            color: pdfColors.muted,
            marginBottom: s(scale, 1),
        },
        metaBlock: {
            alignItems: 'flex-end',
        },
        metaLine: {
            fontSize: s(scale, 8),
            marginBottom: s(scale, 2),
        },
        sectionTitle: {
            fontSize: s(scale, 10),
            fontWeight: 'bold',
            marginTop: s(scale, 10),
            marginBottom: s(scale, 6),
            color: pdfColors.accent,
        },
        sectionTitleCompact: {
            fontSize: s(scale, 10),
            fontWeight: 'bold',
            marginTop: s(scale, 6),
            marginBottom: s(scale, 6),
            color: pdfColors.accent,
        },
        sectionDivider: {
            marginTop: s(scale, 6),
            borderTopWidth: 1,
            borderTopColor: pdfColors.border,
        },
        twoCol: {
            flexDirection: 'row',
            gap: s(scale, 18),
        },
        col: {
            flex: 1,
        },
        table: {
            marginTop: s(scale, 4),
            borderWidth: 1,
            borderColor: pdfColors.border,
        },
        tableHead: {
            flexDirection: 'row',
            backgroundColor: '#faf7f2',
            borderBottomWidth: 1,
            borderBottomColor: pdfColors.border,
            paddingVertical: s(scale, 4),
            paddingHorizontal: s(scale, 6),
        },
        tableRow: {
            flexDirection: 'row',
            borderBottomWidth: 1,
            borderBottomColor: pdfColors.border,
            paddingVertical: s(scale, 4),
            paddingHorizontal: s(scale, 6),
        },
        thName: { width: '44%' },
        itemCell: {
            width: '44%',
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: s(scale, 6),
            paddingRight: s(scale, 4),
        },
        itemThumb: {
            width: s(scale, 34),
            height: s(scale, 34),
            objectFit: 'cover',
        },
        itemThumbPlaceholder: {
            width: s(scale, 34),
            height: s(scale, 34),
            backgroundColor: '#f2ebe3',
            borderWidth: 1,
            borderColor: '#e8e0d6',
        },
        itemTextCol: {
            flex: 1,
            flexGrow: 1,
        },
        itemName: {
            fontSize: s(scale, 8),
            fontWeight: 'bold',
            textTransform: 'uppercase',
            marginBottom: s(scale, 2),
        },
        itemDesc: {
            fontSize: s(scale, 7),
            color: '#5c534c',
            lineHeight: 1.25,
        },
        /** Delivery / missing note line tables (no price columns): ~60% / 25% / 15% */
        thNoteItem: { width: '60%' },
        thNoteSku: { width: '25%' },
        thNoteQty: { width: '15%', textAlign: 'right' },
        thSku: { width: '22%' },
        thQty: { width: '10%', textAlign: 'right' },
        thPrice: { width: '12%', textAlign: 'right' },
        thTotal: { width: '12%', textAlign: 'right' },
        tdMuted: {
            fontSize: s(scale, 8),
            color: pdfColors.muted,
        },
        labelValueRow: {
            flexDirection: 'row',
            marginBottom: s(scale, 3),
            alignItems: 'flex-start',
        },
        label: {
            width: '42%',
            fontSize: s(scale, 8),
            color: pdfColors.muted,
        },
        value: {
            width: '58%',
            fontSize: s(scale, 8),
        },
    });
}

/** Default scale (1) — used where scale is not passed. */
export const pdfStyles = createPdfStyles(1);
